import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Plus, Minus } from "lucide-react";
import { fetchAllProducts } from "../../actions/product";
import { classList, studentTypeList } from "../../utils/constants";
import Select from 'react-select';
import { Tooltip } from "@mui/material";


const BundleFormDialog = ({ data, onSave, onCancel, header, selectedYear }) => {

  const [formData, setFormData] = useState(() => {
    if (data) {
      return { 
        ...data,
        applicableClassesArray: data.applicableClasses ? data.applicableClasses.split(", ").map(cls => {
          const classItem = classList.find(c => c.label === cls || c.key === cls);
          return classItem ? { value: classItem.key, label: classItem.label } : { value: cls, label: cls };
        }).filter(Boolean) : [],
        applicableStudentTypesArray: data.applicableStudentTypes ? data.applicableStudentTypes.split(", ").map(st => {
          const stItem = studentTypeList.find(s => s.label === st || s.key === st);
          return stItem ? { value: stItem.key, label: stItem.label } : { value: st, label: st };
        }).filter(Boolean) : (data.studentType ? [{ value: data.studentType, label: data.studentType }] : [])
      };
    }

    return {
      name: "",
      display_name: "",
      imageUrl: "",
      academicYear: "",
      gender: "",
      studentType: "",
      applicableStudentTypes: "",
      applicableStudentTypesArray: [],
      applicableClasses: "",
      applicableClassesArray: [],
      totalPrice: 0,
      isIndividualProduct: false,
      isActive: false,
      products: [{ productId: "", quantity: 1, max_quantity: 1, optional: false, selection_group: false, order: 1 }]
    };
  });
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const genderList = [
    { key: "Boy", label: "Boy" },
    { key: "Girl", label: "Girl" },
  ];
  
  useEffect(() => {
    const loadProducts = async () => {
      setLoading(true);
      try {
        const response = await fetchAllProducts();
        setProducts(Array.isArray(response) ? response : []);
      } catch (error) {
        setProducts([]);
        toast.error("Failed to load products", { position: "top-right" });
      } finally {
        setLoading(false);
      }
    };
    loadProducts();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSelectChange = (selectedOptions, actionMeta) => {
    if (actionMeta.name === "applicableClassesArray") {
      const selectedClasses = selectedOptions || [];
      const classesString = selectedClasses.map(option => option.label).join(", ");
      
      setFormData({ 
        ...formData,
        applicableClassesArray: selectedClasses,
        applicableClasses: classesString
      });
    } else if (actionMeta.name === "applicableStudentTypesArray") {
      const selectedTypes = selectedOptions || [];
      const typesString = selectedTypes.map(option => option.label || option.value).join(", ");
      const primaryType = selectedTypes.length > 0 ? (selectedTypes[0].value || selectedTypes[0].label) : "";
      
      setFormData({
        ...formData,
        applicableStudentTypesArray: selectedTypes,
        applicableStudentTypes: typesString,
        studentType: primaryType || formData.studentType
      });
    }
  };

  const handleProductChange = (index, field, value) => {
    const updatedProducts = [...formData.products];
    
    // If changing product ID, check for duplicates
    if (field === "productId" && value) {
      const isDuplicate = updatedProducts.some((p, i) => 
        i !== index && p.productId === value
      );
      
      if (isDuplicate) {
        const duplicateProduct = products.find(p => p.id === parseInt(value));
        toast.error(`Product "${duplicateProduct.name}" is already selected`, { position: "top-right" });
        return;
      }
    }
    
    updatedProducts[index] = { ...updatedProducts[index], [field]: value };
    
    // Auto-calculate total price when product changes
    const totalPrice = calculateTotalPrice(updatedProducts);

    setFormData({
      ...formData,
      products: updatedProducts,
      totalPrice
    });
  };

  const calculateTotalPrice = (bundleProducts) => {
    return bundleProducts.reduce((sum, item) => {
      const product = products.find(p => p.id === parseInt(item.productId));
      let price = product? product.price : 0;
      if(!formData.isIndividualProduct){
        price = product ? product.bundle_price : 0
      }
      return sum + (product ? price * item.quantity : 0);
    }, 0);
  };

  const addProductField = () => {

    const isIndividualProduct = formData.isIndividualProduct ? true: false;
    setFormData(prev => ({
      ...prev,
      isIndividualProduct: isIndividualProduct,
      products: [
        ...(Array.isArray(prev.products) ? prev.products : []),
        { productId: "", quantity: 1, max_quantity:1, optional: false, selection_group: false, order:1 }
      ]
    }));
  };

  const removeProductField = (index) => {
    const updatedProducts = formData.products.filter((_, i) => i !== index);
    const totalPrice = calculateTotalPrice(updatedProducts);
    
    setFormData({
      ...formData,
      products: updatedProducts,
      totalPrice
    });
  };

  const toggleOptional = (index) => {
    const updatedProducts = [...formData.products];
    updatedProducts[index] = { 
      ...updatedProducts[index], 
      optional: !updatedProducts[index].optional 
    };
    setFormData({
      ...formData,
      products: updatedProducts
    });
  };

    const togglesSlection_group = (index) => {
    const updatedProducts = [...formData.products];
    updatedProducts[index] = { 
      ...updatedProducts[index], 
      selection_group: !updatedProducts[index].selection_group 
    };
    setFormData({
      ...formData,
      products: updatedProducts
    });
  };

  const checkDuplicateProducts = () => {
    const productIds = formData.products
      .filter(p => p.productId) // Filter out empty selections
      .map(p => p.productId);
    
    const duplicates = productIds.filter((id, index) => productIds.indexOf(id) !== index);
    
    if (duplicates.length > 0) {
      const duplicateProduct = products.find(p => p.id === parseInt(duplicates[0]));
      if (duplicateProduct) {
        toast.error(`Product "${duplicateProduct.name}" is selected multiple times`, { position: "top-right" });
      } else {
        toast.error("Same product is selected multiple times", { position: "top-right" });
      }
      return true;
    }
    
    return false;
  };

  const isFormValid = () => {
    return (
      !!formData.name &&
      !!formData.display_name &&
      !!formData.imageUrl &&
      !!formData.gender &&
      (!!formData.studentType || (formData.applicableStudentTypesArray && formData.applicableStudentTypesArray.length > 0)) &&
      !!formData.academicYear &&
      !!formData.applicableClasses &&
      Array.isArray(formData.products) &&
      formData.products.length > 0 &&
      formData.products.every(
        (p) => p.productId && Number(p.quantity) >= 0
      ) &&
      formData.products.every(
        (p) => p.productId && Number(p.order) >= 0
      ) &&
      typeof formData.isIndividualProduct === "boolean" &&
      typeof formData.isActive === "boolean"
    );
  };

  const handleFormSubmit = () => {
    if (checkDuplicateProducts()) {
      return;
    }
    
    const preparedData = {
      ...formData,
    };
    
    onSave(preparedData);
  };

  const classOptions = classList.map(cls => ({ value: cls.key, label: cls.label }));
  const studentTypeOptions = studentTypeList.map(st => ({ value: st.key, label: st.label }));

  const productOptions = products.map((p) => ({
    value: p.id,
    label: `${p.name} - Unit Price : ₹${p.price} -Bundle Price : ₹${p.bundle_price} ${p.display_name}`,
  }));


  return (
    <div
      className="modal fade show d-block"
      tabIndex="-1"
      role="dialog"
      style={{ background: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog modal-xl" role="document">
        <div className="modal-content">
          <div className="modal-header justify-content-between">
            <h5 className="modal-title">
              {header || "Bundle Form"}
            </h5>
            <button
              type="button"
              className="close"
              onClick={onCancel}
            >
              <span>&times;</span>
            </button>
          </div>
          <div className="modal-body">
            {loading ? (
              <div className="text-center">Loading products...</div>
            ) : (
              <form>
                <div className="row mb-3">
                  <div className="col-md-3">
                    <div className="form-group">
                      <label>Bundle Name *</label>
                      <input
                        type="text"
                        className="form-control"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Enter bundle name"
                        required
                      />
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="form-group">
                      <label>Bundle Display Name *</label>
                      <input
                        type="text"
                        className="form-control"
                        name="display_name"
                        value={formData.display_name}
                        onChange={handleChange}
                        placeholder="Enter Bundle Display Name"
                        required
                      />
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="form-group">
                      <label>Academic Year *</label>
                      <select
                        className="form-control"
                        name="academicYear"
                        value={formData.academicYear}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select Year</option>
                          <option key={selectedYear} value={selectedYear}>
                            {selectedYear}
                          </option>
                      </select>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="form-group">
                      <label>Image URL *</label>
                      <input
                        type="text"
                        className="form-control"
                        name="imageUrl"
                        value={formData.imageUrl}
                        onChange={handleChange}
                        placeholder="Enter image URL"
                        required
                      />
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="form-group">
                      <label>Gender *</label>
                      <select
                        className="form-control"
                        name="gender"
                        value={formData.gender}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select Gender</option>
                        {genderList.map(option => (
                          <option key={option.key} value={option.key}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Student Type *</label>
                      {formData.isIndividualProduct ? (
                        <Select
                          isMulti
                          name="applicableStudentTypesArray"
                          value={formData.applicableStudentTypesArray}
                          options={studentTypeOptions}
                          className="basic-multi-select"
                          classNamePrefix="select"
                          onChange={handleSelectChange}
                          placeholder="Select student types"
                        />
                      ) : (
                        <select
                          className="form-control"
                          name="studentType"
                          value={formData.studentType}
                          onChange={handleChange}
                          required
                        >
                          <option value="">Select Student Type</option>
                          {studentTypeList.map(option => (
                            <option key={option.key} value={option.key}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Applicable Classes *</label>
                      <Select
                        isMulti
                        name="applicableClassesArray"
                        value={formData.applicableClassesArray}
                        options={classOptions}
                        className="basic-multi-select"
                        classNamePrefix="select"
                        onChange={handleSelectChange}
                        placeholder="Select classes"
                      />
                      <input 
                        type="hidden" 
                        name="applicableClasses" 
                        value={formData.applicableClasses} 
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group">
                      <label className="font-weight-bold">Total Price</label>
                      <input
                        type="text"
                        className="form-control"
                        value={`₹${ formData.totalPrice ? formData.totalPrice?.toFixed(2) : 0}`}
                        disabled
                      />
                    </div>
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-md-8">
                    <div className="form-group mt-2 d-flex align-items-center">
                      <label className="font-weight-bold d-block mb-1 me-2">Status</label>
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="isActive"
                          name="isActive"
                          checked={formData.isActive || false}
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              isActive: e.target.checked,
                            });
                          }}
                        />
                        <label className="form-check-label" htmlFor="isActive">
                          {formData.isActive ? "Active" : "Inactive"}
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row mb-3"> 
                  { formData?.products?.length > 0 && (
                  <div className="col-md-8">
                    <div className="form-group mt-2 d-flex align-items-center">
                      <label className="font-weight-bold d-block mb-1 me-2">Is Individual Product</label>
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="isIndividualProduct"
                          name="isIndividualProduct"
                          checked={formData.isIndividualProduct || false}
                          onChange={(e) => {
                            // If switching to individual product, set all products as non-optional
                            let updatedProducts = [...formData.products];
                            if (e.target.checked) {
                              updatedProducts = [
                                {
                                  ...updatedProducts[0],
                                  optional: false,
                                  selection_group:false
                                }
                              ];
                            }
                            
                            setFormData({
                              ...formData,
                              isIndividualProduct: e.target.checked,
                              products: updatedProducts
                            });
                          }}
                        />
                        <label className="form-check-label" htmlFor="isIndividualProduct">
                          {formData.isIndividualProduct ? "Yes" : "No"}
                        </label>
                      </div>
                    </div>
                  </div>
                  )}
                </div>

                <div className="row mt-4">
                  <div className="col-12">
                    <h5>Bundle Products</h5>
                    <p className="text-muted small">Add products to this bundle</p>
                  </div>
                </div>
                
                <div className="row mb-2 fw-bold border-bottom pb-2">
                  <div className="col-md-5">Product {formData?.products?.length}</div>
                  <div className="col-md-1">Order</div>
                  <div className="col-md-1">{formData.isIndividualProduct ? "MOQ" : "Quantity"}</div>
                  {!formData.isIndividualProduct &&
                    <div className="col-md-1">Max Quantity</div>
                  }
                  <div className="col-md-2">Total</div>
                  <div className="col-md-1">GroupBy / Optional</div>
                  <div className="col-md-1">Actions</div>
                </div>
                
                {formData.products?.map((product, index) => (
                  <div className="row mb-2 align-items-center" key={index}>
                    <Tooltip title={
                          productOptions.find(
                            (option) => String(option.value) === String(product.productId)
                          )?.label || ""
                        } arrow>
                    <div className="col-md-5">
                      <Select
                        options={productOptions}
                        value={
                          productOptions.find(
                            (option) => String(option.value) === String(product.productId)
                          ) || null
                        }
                        onChange={(selected) =>
                          handleProductChange(index, "productId", selected?.value)
                        }
                        placeholder="Select Product"
                        isClearable
                        isSearchable
                      />
                    </div>
                    </Tooltip>
                    <div className="col-md-1">
                      <input
                        type="number"
                        className="form-control"
                        value={product.order}
                        onChange={(e) =>
                          handleProductChange(
                            index,
                            "order",
                            parseInt(e.target.value) || 0
                          )
                        }
                        min="0"
                        required
                      />
                    </div>
                    <div className="col-md-1">
                      <input
                        type="number"
                        className="form-control"
                        value={product.quantity}
                        onChange={(e) =>
                          handleProductChange(
                            index,
                            "quantity",
                            parseInt(e.target.value) || 0
                          )
                        }
                        min="0"
                        required
                      />
                    </div>
                    {!formData.isIndividualProduct && (
                    <div className="col-md-1">
                      <input
                        type="number"
                        className="form-control"
                        value={product.max_quantity ?? product.quantity}
                         onChange={(e) => {
                            let value = parseInt(e.target.value) || product.quantity;

                            // // Enforce minimum
                            if (value < product.quantity) {
                              value = product.quantity;
                            }

                            handleProductChange(index, "max_quantity", value);
                          }}
                        min={product.quantity}
                        required
                      />
                    </div>
                    )}
                    <div className={!formData.isIndividualProduct ? "col-md-2" : "col-md-2"}>
                      {(() => {
                        const selectedProduct = products.find(p => String(p.id) === String(product.productId));
                        if (!selectedProduct) return `₹0.00`;
                        
                        if (!formData.isIndividualProduct) {
                            const total = product.quantity * selectedProduct.bundle_price;
                            return `${product.quantity} x ₹${selectedProduct.bundle_price}= ₹${total}`;
                        } else {
                            const total = product.quantity * selectedProduct.price;
                            return `${product.quantity} x ₹${selectedProduct.price}= ₹${total}`;
                        }
                      })()}
                    </div>
                    <div className="col-md-1">
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={product.selection_group}
                          onChange={() => togglesSlection_group(index)}
                          disabled={formData.isIndividualProduct}
                          id={`selection_group-${index}`}
                        />
                        <label className="form-check-label" htmlFor={`optional-${index}`}>
                          Group
                        </label>
                      </div>
                       <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={product.optional}
                          onChange={() => toggleOptional(index)}
                          disabled={formData.isIndividualProduct}
                          id={`optional-${index}`}
                        />
                        <label className="form-check-label" htmlFor={`optional-${index}`}>
                          Optional
                        </label>
                      </div>
                    </div>
                    <div className="col-md-1">
                      <button
                        type="button"
                        className="btn btn-sm btn-danger me-1"
                        onClick={() => removeProductField(index)}
                        disabled={formData.products.length === 1 || formData.isIndividualProduct}
                      >
                        <Minus size={16} />
                      </button>
                      {index === formData.products.length - 1 && (
                        <button
                          type="button"
                          className="btn btn-sm btn-success"
                          onClick={addProductField}
                          disabled={formData.isIndividualProduct}
                        >
                          <Plus size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {!formData.products && (
                  <button
                    type="button"
                    className="btn btn-sm btn-success"
                    onClick={addProductField}
                    disabled={formData.isIndividualProduct}
                  >
                    <Plus size={16} />
                  </button>
                )}
              </form>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-danger" onClick={onCancel}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleFormSubmit}
              disabled={!isFormValid() || loading}
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BundleFormDialog; 